import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { MonitoringEventTypeType } from "../../shared/types/monitoring";
import { useAv, useAvPubsubMessaging } from "../../utils/av";
import { useUserCourseSettings } from "../../utils/course";

import { BaseFooter } from "./BaseFooter";
import { JoinInPersonClassButton } from "./JoinInPersonClassButton";
import LeaveClassButton from "./LeaveClassButton";
import { AIPanelToggleButton } from "./controls/AIPanelToggleButton";
import { ChatPanelToggleButton } from "./controls/ChatPanelToggleButton";
import { UserPanelToggleButton } from "./controls/UserPanelToggleButton";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import { ScreenshareOptions } from "components/av/ScreenshareOptions";
import { useVirtualClassActions } from "components/av/virtual-class-utils";
import { PillBanner } from "components/footer/PillBanner";
import { StudentPreviewOptions } from "components/footer/StudentPreviewOptions";
import { PresentationOptions } from "components/materials/presentation/PresentationOptions";
import { ReactionMenu } from "components/menus/ReactionMenu";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { PillButton } from "mds/components/PillButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { HandRaiseIcon, HandRaiseOutlineIcon, VideoIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectCurrentCourseAllowStudentsToJoinAv,
  selectIsClassSessionInPerson,
  selectIsPracticeClassSession,
  selectIsStudentPreview,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import {
  selectCurrentClassUser,
  selectCurrentClassUserIsInPersonClass,
} from "store/selectors/class-user";
import {
  selectCanStudentsScreenshare,
  selectScreensharingClassUserId,
} from "store/selectors/virtual-classes";
import { useCreateMonitoringEvent } from "utils/hooks/use-create-monitoring-event";
import { usePresentation } from "utils/presentation";

const LazyScreenshareButton = withActiveAv(() => import("components/av/ScreenshareButton"));
const LazyVideoMicControls = withActiveAv(() => import("components/av/VideoMicControls"));
const LazyBreakoutsBanner = withActiveAv(
  () => import("components/footer/controls/BreakoutsBanner"),
);

const ANIMATION_DURATION = 500;

interface StudentMainFooterProps {
  selectedPage: PageType;
}

export const StudentMainFooter: FC<StudentMainFooterProps> = ({ selectedPage }) => {
  const isMobile = !useIsMdOrLarger();
  const { canSeeAIPanel } = useUserCourseSettings();
  const allowStudentsToJoinAv = useAppSelector(selectCurrentCourseAllowStudentsToJoinAv);
  const isStudentPreview = useAppSelector(selectIsStudentPreview);
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const currentClassUser = useAppSelector(selectCurrentClassUser);
  const canStudentsScreenshare = useAppSelector(selectCanStudentsScreenshare);
  const { presentedPageId } = usePresentation();
  const isBreakoutActive = useAppSelector(selectIsInBreakout);
  const isClassSessionInPerson = useAppSelector(selectIsClassSessionInPerson);
  const {
    isInVirtualClass,
    isAvActiveInAnotherTab,
    isVirtualClassActive,
    isHandRaised,
    toggleHand,
  } = useAv();
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isInClassSession = isInVirtualClass || isInInPersonClass;
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);
  const isInBreakoutRoom =
    isBreakoutActive && isInVirtualClass && !!currentClassUser?.breakout_group_id;

  // Animation state for presentation/screenshare banner reveal/hide and content changes
  // Enter: offset (instant, w-0) → animating (transition to measured width) → idle
  // Exit: exitOffset (instant, at width) → exitAnimating (transition to 0) → idle
  const prevPresentedPageId = useRef<string | null>(null);
  const prevScreensharingClassUserId = useRef<string | null>(null);
  type AnimationPhase = "idle" | "offset" | "animating" | "exitOffset" | "exitAnimating";
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>("idle");
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const bannerContentRef = useRef<HTMLDivElement | null>(null);
  const [bannerWidth, setBannerWidth] = useState<number | null>(null);

  // Use useLayoutEffect to set initial state BEFORE browser paints
  useLayoutEffect(() => {
    const wasBannerVisible =
      !!prevPresentedPageId.current || !!prevScreensharingClassUserId.current;
    const isBannerVisible = !!presentedPageId || !!screensharingClassUserId;

    // Entering: banner not visible → banner visible (while in class)
    const isEntering = isInClassSession && !wasBannerVisible && isBannerVisible;

    if (isEntering) {
      // Clear any existing animation before starting new one
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);

      // Measure the banner content width before animating
      if (bannerContentRef.current) {
        setBannerWidth(bannerContentRef.current.scrollWidth);
      }

      // Phase 1: Apply left offset instantly (buttons stay at original position)
      setAnimationPhase("offset");

      // Phase 2: Next frame - remove offset with transition → buttons animate right
      requestAnimationFrame(() => {
        setAnimationPhase("animating");
      });

      // Phase 3: After animation completes, show banner
      animationTimeoutRef.current = setTimeout(() => {
        setAnimationPhase("idle");
        animationTimeoutRef.current = null;
      }, ANIMATION_DURATION);

      // Exiting: banner visible → banner not visible (while in class)
    } else if (isInClassSession && wasBannerVisible && !isBannerVisible) {
      // Clear any existing animation before starting new one
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);

      // Measure current width before exit animation
      if (bannerContentRef.current) {
        setBannerWidth(bannerContentRef.current.scrollWidth);
      }

      // Phase 1: Apply right offset instantly (buttons stays in place despite banner disappearing)
      setAnimationPhase("exitOffset");

      // Phase 2: Next frame - remove offset with transition → buttons animate left
      requestAnimationFrame(() => {
        setAnimationPhase("exitAnimating");
      });

      // After animation completes, return to idle
      animationTimeoutRef.current = setTimeout(() => {
        setAnimationPhase("idle");
        animationTimeoutRef.current = null;
      }, ANIMATION_DURATION);
    }

    // Safety: if we're rendering the animated banner but stuck in a non-idle state
    // without an active animation timeout, reset to idle to ensure visibility
    const shouldShowAnimatedBanner =
      screensharingClassUserId || (isInClassSession && !isInBreakoutRoom);
    if (shouldShowAnimatedBanner && animationPhase !== "idle" && !animationTimeoutRef.current) {
      setAnimationPhase("idle");
    }

    prevPresentedPageId.current = presentedPageId ?? null;
    prevScreensharingClassUserId.current = screensharingClassUserId ?? null;
  }, [
    presentedPageId,
    screensharingClassUserId,
    isInClassSession,
    isInBreakoutRoom,
    animationPhase,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    };
  }, []);

  const createMonitoringEvent = useCreateMonitoringEvent();
  useEffect(() => {
    if (isVirtualClassActive && isAvActiveInAnotherTab) {
      createMonitoringEvent(MonitoringEventTypeType.AV_PRESENT_IN_ANOTHER_TAB, {});
    }
  }, [isVirtualClassActive, isAvActiveInAnotherTab, createMonitoringEvent]);

  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();

  const { showAvCheckToJoinVirtualClass } = useVirtualClassActions();

  const onJoinVirtualClass = useCallback(() => {
    showAvCheckToJoinVirtualClass();
  }, [showAvCheckToJoinVirtualClass]);

  const onMoveVirtualClassToThisTab = useCallback(() => {
    sendPubsubVirtualClassStateMessage({
      action: "join",
      force: true,
    });
  }, [sendPubsubVirtualClassStateMessage]);

  const MaybeAIPanel = canSeeAIPanel && !isMobile ? <AIPanelToggleButton /> : null;

  const MaybeJoinVirtualClassButton =
    isVirtualClassActive &&
    !isPracticeClassSession &&
    !isInVirtualClass &&
    allowStudentsToJoinAv &&
    !isMobile ? (
      <PillBanner
        buttonChildren={
          isAvActiveInAnotherTab ? (
            <PillButton
              eventName="Move Virtual Class to This Tab"
              kind="blue"
              size="s"
              title={t("av.move_virtual_class_to_this_tab")}
              onClick={onMoveVirtualClassToThisTab}
            >
              {t("av.move_virtual_class_to_this_tab")}
            </PillButton>
          ) : (
            <PillButton
              eventName="Enter class"
              kind="blue"
              size="s"
              title={t("footer.join_session")}
              onClick={onJoinVirtualClass}
            >
              {t("footer.join_session")}
            </PillButton>
          )
        }
        kind="blue"
        textChildren={
          <IconText
            iconStart={<VideoIcon className="text-blue-shade-40" />}
            text={
              isAvActiveInAnotherTab
                ? t("av.virtual_class_in_another_tab")
                : t("av.virtual_class_in_session")
            }
            truncateText
          />
        }
      />
    ) : null;

  const MaybeJoinInPersonClassButton =
    isClassSessionInPerson && !isPracticeClassSession && !isInInPersonClass ? (
      <JoinInPersonClassButton />
    ) : null;

  const MaybeStopStudentPreviewBanner = isStudentPreview ? <StudentPreviewOptions /> : null;

  if (
    !isVirtualClassActive &&
    !isClassSessionInPerson &&
    !presentedPageId &&
    !MaybeJoinVirtualClassButton &&
    !MaybeJoinInPersonClassButton &&
    !MaybeStopStudentPreviewBanner &&
    !MaybeAIPanel
  ) {
    return null;
  }

  const RightSidePanel = (
    <>
      {MaybeStopStudentPreviewBanner}
      {isVirtualClassActive && isInVirtualClass && (
        <Button
          eventName={isHandRaised ? "Lower hand" : "Raise hand"}
          isSelected={isHandRaised}
          kind="squareLegacy"
          size="l"
          title={t(isHandRaised ? "av.lower_hand" : "av.raise_hand")}
          isSquare
          onClick={toggleHand}
        >
          {isHandRaised ? <HandRaiseIcon /> : <HandRaiseOutlineIcon />}
        </Button>
      )}

      {isInClassSession && (
        <>
          <ReactionMenu />
          {isInVirtualClass &&
            currentClassUser?.id !== screensharingClassUserId &&
            canStudentsScreenshare && <LazyScreenshareButton />}
          <ChatPanelToggleButton isLarge />
        </>
      )}

      <UserPanelToggleButton />
      {MaybeAIPanel}
    </>
  );

  return (
    <BaseFooter darkMode={isInClassSession}>
      <div
        className={twMerge(
          "flex h-full w-full items-center",
          isInClassSession && "justify-between",
        )}
      >
        <LazyVideoMicControls />

        <div
          className={twMerge(
            "flex h-full w-full items-center gap-2",
            !isInClassSession ? "justify-end" : "justify-center",
          )}
        >
          {MaybeJoinVirtualClassButton}
          {MaybeJoinInPersonClassButton}
          {isVirtualClassActive && isInVirtualClass && isBreakoutActive && <LazyBreakoutsBanner />}

          {screensharingClassUserId || (isInClassSession && !isInBreakoutRoom) ? (
            //* Here we animate banner width which naturally pushes buttons
            <div className="flex items-center justify-end gap-2">
              {/* Banner with animated width - content invisible until animation completes */}
              <div
                className={twMerge(
                  "overflow-hidden",
                  // Transition width during animating phases
                  (animationPhase === "animating" || animationPhase === "exitAnimating") &&
                    "transition-[width] duration-700 ease-out",
                )}
                style={{
                  // Use measured width for precise animation
                  width:
                    animationPhase === "offset" || animationPhase === "exitAnimating"
                      ? 0
                      : animationPhase === "animating" || animationPhase === "exitOffset"
                        ? (bannerWidth ?? undefined)
                        : undefined,
                }}
              >
                <div
                  className={twMerge(animationPhase !== "idle" && "invisible")}
                  ref={bannerContentRef}
                >
                  {screensharingClassUserId ? (
                    <ScreenshareOptions autoFollow={isInClassSession} />
                  ) : (
                    <PresentationOptions selectedPage={selectedPage} isInClassSession />
                  )}
                </div>
              </div>

              {/* Buttons - no animation needed, flex naturally repositions them */}
              <div className="flex items-center gap-2">{RightSidePanel}</div>
            </div>
          ) : (
            <>
              {isInClassSession && !isInBreakoutRoom && (
                <PresentationOptions selectedPage={selectedPage} />
              )}

              {RightSidePanel}
            </>
          )}
        </div>

        {isInClassSession && <LeaveClassButton />}
      </div>
    </BaseFooter>
  );
};
